import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
} from 'recharts';
import { getZoneColor, getZoneFromHeartRate } from '@trainme/api/client';

import { ActivityWithRelations } from '@/utils/types';
import type { Lap } from '@trainme/db/client';
import { trpc } from '@/app/api/trpc/client';
import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  activity: ActivityWithRelations;
};

export function ActivityChart({ activity }: Props) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const { data: zones } = trpc.settings.getTrainingZones.useQuery();
  const hasLaps = Boolean(activity?.laps && activity.laps.length > 0);

  const lapData = useMemo(() => {
    if (!hasLaps || !activity?.laps) return [];
    return activity.laps.map((lap: Lap, index: number) => ({
      name: `${index + 1}`,
      distance: lap.distance,
      averageHeartrate: lap.averageHeartrate,
      averageSpeed: lap.averageSpeed,
      duration: lap.movingTime,
      hearrateZone: getZoneFromHeartRate(
        lap.averageHeartrate || 0,
        zones || []
      ),
    }));
  }, [activity, zones, hasLaps]);

  useEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setChartSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const chartHeight = chartRef.current?.getBoundingClientRect().height || 10;
  // fontsize between 8 and 12, otherwise propotional to chart height
  const fontSize = Math.max(8, Math.min(Math.round(chartHeight / 5), 13));

  if (!hasLaps) return <></>;

  return (
    <div ref={chartRef} className='relative w-full h-full'>
      {chartSize.width > 0 && chartSize.height > 0 && (
        <>
          <ResponsiveContainer
            width='100%'
            height='100%'
            className='absolute top-0 left-0'
            minWidth={1}
            minHeight={1}
          >
            <BarChart
              data={lapData}
              margin={{ top: 0, right: -1, bottom: -10, left: -1 }}
              key={`activity-${activity.id}`}
              barCategoryGap={1}
            >
              <XAxis dataKey='name' fontSize={fontSize} />
              <Bar dataKey='hearrateZone'>
                {lapData?.map((entry, index) => (
                  <Cell
                    key={`cell-${activity.id}-${index}-${entry.distance}-${entry.averageHeartrate}`}
                    fill={getZoneColor(entry.hearrateZone)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <ResponsiveContainer
            width='100%'
            height='100%'
            className='absolute top-0 left-0'
            minWidth={1}
            minHeight={1}
          >
            <LineChart
              key={`activity-${activity.id}`}
              data={lapData}
              className='relative'
              margin={{ top: 5, right: 15, bottom: -10, left: 15 }}
            >
              <XAxis dataKey='name' className='hidden' />
              <Line
                type='monotone'
                dataKey='averageSpeed'
                stroke='#197441'
                strokeWidth={2}
                dot={false}
                key={`speed-${activity.id}`}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
